import BaseView, { DomainModel3D } from './base-view';
import ModelLoader from '../model-loader';
import assets from '../assets';
import getLogger from '../logging';
import hitPlaneMaterial from '../view3d/hit-plane-material';
import mfac from '../material-factory';
import visibleSize from '../view3d/visible-size';
import { Box3, Material, Mesh, Object3D, PlaneGeometry } from 'three';
import { GenericAsset } from '../domain';
import { sortMaterialsBySlot } from '../controllers/material-controllers';
import { zip } from '../control-flow/zip';

const ml = ModelLoader.getInstance();
const log = getLogger('views');


export default class GenericAssetView extends BaseView {

    declare model: GenericAsset;
    root: Object3D;
    hit: Mesh;
    hitBox: Box3 = new Box3();

    constructor(model: DomainModel3D) {
        super(model);

        this.name = 'Generic Asset';
        this.userData.isGenericAsset = true;
        this.userData.suppressExport = true;

        this.onReady.name = 'GenericAssetView';
        this.init();
    }

    make() {
        this.hit = new Mesh(new PlaneGeometry(), hitPlaneMaterial);
        this.hit.name = 'GenericAsset hit rectangle';
        this.hit.visible = false;
        this.hit.userData.isSelectable = true;
        this.hit.userData.isMovable = true;
        this.add(this.hit);
    }

    bind() {
        const updateAppearance = this.updateAppearance.bind(this);
        this.model.bind('assetId', updateAppearance);
        this.model.bind('filename', updateAppearance);
        this.model.on('width', updateAppearance);

        this.model.on('opacity', () => {
            const castShadow = this.model.opacity == 1;
            this.root?.traverse(obj => {
                if(obj["isMesh"]) obj.castShadow = castShadow;
            });
        });
    }

    updateAppearance() {
        this.updates.queue(
            () => this.loadAsset(this.model),
            (object: Object3D) => {

                // TODO: remove this after scene.background update
                const
                    isMirror = [
                        "obstructions/mirror-1",
                        "obstructions/mirror-2",
                    ].includes(this.model.assetId),
                    isLight = this.model.type == "light",
                    isBacking = this.model.type == "backing";

                this.highlights.clear();
                object.traverse(obj => {
                    if(obj instanceof Mesh) {
                        this.highlights.add(obj);

                        // TODO: remove this after scene.background update
                        if(isMirror) {
                            obj.material.envMap = mfac.textureCube;
                        }

                        if(isLight) {
                            obj.userData.isLightMesh = true;
                        }

                        if(isBacking) {
                            obj.userData.isBackingMesh = true;
                        }
                    }
                });
                this.root?.removeFromParent();
                this.root = object;
                this.add(this.root);

                visibleSize(this.root, this.hitBox);

                const width = this.hitBox.max.x - this.hitBox.min.x;
                const height = this.hitBox.max.y - this.hitBox.min.y;

                this.hit.scale.set(width, height, 1);
                this.hitBox.getCenter(this.hit.position);

                this.onReady.trigger();

                // We have a handler setup in the bind method that does this
                // too, but we need it here to handle initial loading
                const castShadow = this.model.opacity == 1;
                this.root?.traverse(obj => {
                    if(obj["isMesh"]) obj.castShadow = castShadow;
                });
                this.setGlbMaterialOpacity();
            });
    }

    /**
     * Loads and sets up materials for generic assets.
     */
    async loadAsset(asset: GenericAsset) {
        let filename: string;
        if(asset.assetId) {
            filename = assets.resolve(asset.assetId, "LowResModel");
        }
        else {
            // old way of getting filename, now deprecated in favor of the
            // assetId field
            filename = asset.filename;
        }
        if(filename.indexOf('#') != -1) {
            const target = this.model.width;
            let closest = 0
            for(const width of this.model.availableWidths) {
                if(width > closest && width <= target) {
                    closest = width;
                }
            }
            // fill in the default value if it's missing somehow
            if(closest == 0) {
                closest = this.model.availableWidths?.length > 0 ?
                    Math.min(...this.model.availableWidths)
                    : 24;
            }
            filename = filename.replace('#', String(closest));
        }
        const object = await ml.get(filename);
        const suffix = filename.split('.').pop().toLowerCase();

        if(asset.filename && asset.filename.includes('RodPD.obj')) {
            for(const [child, material] of zip(object.children, asset.materials)) {
                this.bindMaterial(child as Mesh, material);
                ((child as Mesh).material as Material).needsUpdate = true;
            }
        }
        else if(suffix === "obj") {

            object.traverse(child => {
                if(child instanceof Mesh && child.geometry) {
                    child.castShadow = true;
                    child.receiveShadow = true;
                    if (asset.materials.length > 1) {
                        this.bindMaterialArray(child, asset.materials);
                    } else if(asset.materials[0]) {
                        this.bindMaterial(child, asset.materials[0]);
                    }
                    child.material.needsUpdate = true;
                }
            });

        }
        else if(suffix === "glb"
                  && asset.materials
                  && asset.materials.length > 0)
        {
            this.bindGlbMaterialArray(object, asset);
        }
        else if(suffix == "glb") {
            asset.off('opacity', this.setGlbMaterialOpacity);
            asset.on('opacity', this.setGlbMaterialOpacity);
        }

        return object;
    };

    /**
     * @deprecated - use BaseView.setTreeOpacity instead
     */
    setGlbMaterialOpacity = () => {
        this.root.traverse(obj => {
            if(obj instanceof Mesh) {
                // Only transparent if the opacity is less than one
                obj.material.transparent = this.model.opacity < 1;
                obj.material.opacity = this.model.opacity;
                obj.visible = this.model.opacity != 0;
            }
        });
    }

    /**
     * @deprecated - use BaseView.bindMaterialTree instead
     */
    bindGlbMaterialArray(object: Object3D, asset: GenericAsset) {
        const meshes: Mesh[] = [];
        object.traverse(child => {
            if(child instanceof Mesh && child.geometry) {
                child.castShadow = true;
                child.receiveShadow = true;
                meshes.push(child);
            }
        });

        const
            slots = meshes.map(m => (m.material as Material).name),
            sorted = sortMaterialsBySlot(slots, asset.materials);

        for(let i = 0; i < sorted.length; i++) {
            if(sorted[i]) {
                sorted[i].bind('change', () => {
                    meshes[i].material = mfac.makefrom(sorted[i]);
                });
                this.model.bind('opacity', () => {
                    const opacity = this.model.get('opacity') as number;
                    meshes[i].material = mfac.makeTransparent(sorted[i], opacity);
                });
            }
        }
    }

}

